import React, { FC, SVGProps, useEffect, useState } from 'react';
import styles from './Cart.module.scss';
import { fetchOrderStatus } from '../../../api/orders';
import { OrderState, PaymentMethod } from '../../types/CartType';
import { UiKey } from '../../../utils/localization/uiStrings';
import { useUiString, useUiTemplate } from '../../../utils/hook/useUiString';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { CheckIcon, ClockIcon, CrossIcon } from './icons';

// What the guest sees once the order is placed.
//
// This screen is the only channel the restaurant has back to the guest,
// so it keeps polling: a confirmation, a refusal and the line the
// kitchen attached to either all arrive here rather than being pushed.

const POLL_INTERVAL_MS = 10_000;

const TERMINAL: ReadonlySet<string> = new Set(['served', 'cancelled']);

const STATE_LABEL: Record<OrderState, UiKey> = {
    new:       'statusNew',
    confirmed: 'statusConfirmed',
    served:    'statusServed',
    cancelled: 'statusCancelled',
};

// The happy path, in order. A cancelled order leaves it entirely rather
// than sitting on a step it never reached.
const TIMELINE: readonly OrderState[] = ['new', 'confirmed', 'served'];

interface HeroLook {
    Icon: FC<SVGProps<SVGSVGElement>>;
    tone: string;
}

const HERO: Record<OrderState, HeroLook> = {
    new:       { Icon: ClockIcon, tone: styles.heroPending },
    confirmed: { Icon: CheckIcon, tone: styles.heroDone },
    served:    { Icon: CheckIcon, tone: styles.heroDone },
    cancelled: { Icon: CrossIcon, tone: styles.heroCancelled },
};

// Mirrors the operator's own preset row: the longest option is
// open-ended, so it is shown with a plus rather than as an exact promise.
const OPEN_ENDED_FROM = 30;

function prepLabel(minutes: number): string {
    return minutes >= OPEN_ENDED_FROM ? `${OPEN_ENDED_FROM}+` : String(minutes);
}

const SETTLEMENT_LABEL: Partial<Record<PaymentMethod, UiKey>> = {
    square: 'paidByCard',
    cash:   'payAtRegister',
    room:   'chargeToRoom',
};

interface IProps {
    orderId: string;
    method: PaymentMethod;
    /** Close and forget the order — only offered once it is over. */
    onDismiss: () => void;
    /** Close but keep it, so the guest can come back to it. */
    onClose: () => void;
}

function asOrderState(value: string): OrderState {
    return value === 'confirmed' || value === 'served' || value === 'cancelled'
        ? value
        : 'new';
}

function timelineStepClass(index: number, current: number): string {
    return [
        styles.timelineStep,
        index <= current && styles.timelineStepDone,
        index === current && styles.timelineStepCurrent,
    ].filter(Boolean).join(' ');
}

export const OrderStatusStep: FC<IProps> = ({ orderId, method, onDismiss, onClose }) => {
    const t = useUiString();
    const tpl = useUiTemplate();
    const [state, setState] = useState<OrderState>('new');
    const [note, setNote] = useState<string | null>(null);
    const [prepMinutes, setPrepMinutes] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const poll = async () => {
            try {
                const status = await fetchOrderStatus(orderId);
                if (cancelled) return;
                const next = asOrderState(status.status);
                setState(next);
                setNote(status.restaurant_note);
                setPrepMinutes(status.prep_minutes);
                // Nothing more will change, so stop asking.
                if (TERMINAL.has(next)) return;
            } catch {
                // A dropped request is not worth showing: the order exists
                // regardless, and the next tick will try again.
            }
            if (!cancelled) timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
        };

        void poll();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [orderId]);

    const settlement = SETTLEMENT_LABEL[method];
    const over = TERMINAL.has(state);
    const { Icon, tone } = HERO[state];
    const current = TIMELINE.indexOf(state);
    const leave = over ? onDismiss : onClose;

    return (
        <>
            <div className={styles.sheetHeader}>
                <span className={styles.headerSlot} />
                <h4 className={styles.sheetTitle}>{t('yourOrder')}</h4>
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={leave}
                    aria-label={t('close')}
                >
                    <CloseIcon className={styles.iconSvg} />
                </button>
            </div>

            <div className={styles.scroll}>
                <div className={styles.hero}>
                    <span className={`${styles.heroIcon} ${tone}`}>
                        <Icon />
                    </span>
                    <h5 className={styles.heroTitle}>{t(STATE_LABEL[state])}</h5>
                    {/* Only meaningful between the promise and the food:
                        a new order has no estimate yet, and a served one has
                        outlived it. */}
                    {state === 'confirmed' && prepMinutes !== null && (
                        <span className={styles.heroEta}>
                            <ClockIcon />
                            {t('readyIn')} {tpl('approxMinutes', { minutes: prepLabel(prepMinutes) })}
                        </span>
                    )}
                </div>

                {state !== 'cancelled' && (
                    <div className={styles.timeline}>
                        {TIMELINE.map((step, index) => (
                            <div key={step} className={timelineStepClass(index, current)}>
                                <span className={styles.timelineDot} />
                                <span className={styles.timelineLabel}>{t(STATE_LABEL[step])}</span>
                            </div>
                        ))}
                    </div>
                )}

                {settlement && (
                    <div className={styles.card}>
                        <div className={styles.infoRow}>
                            <span>{t('payment')}</span>
                            <span>{t(settlement)}</span>
                        </div>
                    </div>
                )}

                {note && (
                    <div className={styles.card}>
                        <span className={styles.statusLabel}>{t('fromRestaurant')}</span>
                        <p className={styles.statusNote}>{note}</p>
                    </div>
                )}
            </div>

            <div className={styles.sheetFooter}>
                <button
                    type="button"
                    className={over ? styles.primaryButton : styles.secondaryButton}
                    onClick={leave}
                >
                    {t('close')}
                </button>
            </div>
        </>
    );
};
